import React, { useMemo, useRef, useEffect } from 'react';
import styles from './CoachCharacter.module.css';

// Pre-define stickers to avoid re-creation on each render
const STICKERS = {
    excited: '../images/stickers/happy.png',
    neutral: '../images/stickers/proud.png',
    concerned: '../images/stickers/angry.png',
    hi: '../images/stickers/hi.png'
};

// Thresholds for mood calculation
const ENGAGEMENT_THRESHOLDS = {
    HIGH: 80,
    MEDIUM: 40
};

const CoachCharacter = ({
    facialEngagement,
    handsVisible,
    goodPosture,
    isRecording,
    activeIntro
}) => {
    // Use ref for score history to avoid re-renders
    const scoreHistoryRef = useRef([]);

    // In CoachCharacter.js, add useEffect to reset history
    useEffect(() => {
        // Reset score history when intro changes
        scoreHistoryRef.current = [];
    }, [activeIntro]);

    // Calculate mood using useMemo to avoid unnecessary recalculations
    const mood = useMemo(() => {
        // Fast path for intro or not recording
        if (activeIntro) return 'hi';
        if (!isRecording) return 'neutral';

        // Calculate overall score
        const overallScore =
            (facialEngagement > ENGAGEMENT_THRESHOLDS.HIGH ? 1 :
                facialEngagement > ENGAGEMENT_THRESHOLDS.MEDIUM ? 0 : -1) +
            (handsVisible ? 1 : -1) +
            (goodPosture ? 1 : -1);

        // Update score history (using ref to avoid re-renders)
        const history = scoreHistoryRef.current;
        history.push(overallScore);
        if (history.length > 5) history.shift();

        // Calculate average score
        const avgScore = history.reduce((a, b) => a + b, 0) / history.length;

        // Determine mood based on average score
        if (avgScore >= 2) return 'excited';
        if (avgScore >= 0) return 'neutral';
        return 'concerned';
    }, [facialEngagement, handsVisible, goodPosture, isRecording, activeIntro]);

    // Determine which sticker to show
    const sticker = mood === 'hi' ? STICKERS.hi : STICKERS[mood];

    // Render the coach character
    return (
        <div className={`${styles.coachContainer} ${isRecording ? styles.visible : ''}`}>
            <div className={`${styles.characterContainer} ${styles[mood]}`}>
                <img
                    src={sticker}
                    alt=""
                    className={styles.characterImage}
                    loading="eager"
                />
            </div>
        </div>
    );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(CoachCharacter);